// Sound effects, from real recordings: gunshots in a wood, a sniper rifle in a field, a tank's
// gun, explosions, a HIMARS rocket leaving its pod, an air-raid siren tested in Devonport, and
// so on. All are CC0 from freesound.org, listed in client/public/sounds/README.md. The C-RAM is
// the exception: its gatling stream is synthesized (see below).
// Each clip is fetched and decoded once, the first time anything plays; shots that land while
// their clip is still loading are dropped. Every shot is a little different in pitch and level
// so a long fight doesn't sound like a loop, each kind of sound has a cap on how many play at
// once, and a compressor on the way out keeps a big fight loud without clipping.
// Browsers only allow sound after a click, and a raid always starts from one.
// All of it starts muted; the Sound button in the title bar turns it on, and the choice is remembered.

/** One clip: where its sound starts, how much of it to play, how loud, how many may overlap, and how much each copy varies. */
interface Clip { start: number; length: number; gain: number; voices: number; spread?: number }

const CLIPS = {
  rifle: { start: 0.62, length: 1.3, gain: 0.5, voices: 6, spread: 0.08 },
  sniper: { start: 0.0, length: 1.35, gain: 0.95, voices: 2, spread: 0.04 },
  cannon: { start: 0.0, length: 3.2, gain: 0.9, voices: 3, spread: 0.06 },
  explosion: { start: 0.35, length: 4.4, gain: 0.85, voices: 4, spread: 0.1 },
  launch: { start: 0.0, length: 4.0, gain: 0.55, voices: 3, spread: 0.08 },
  intercept: { start: 0.28, length: 1.4, gain: 0.9, voices: 4, spread: 0.1 },
  emp: { start: 0.0, length: 3.1, gain: 0.55, voices: 1 },
  gore: { start: 0.02, length: 0.2, gain: 0.55, voices: 4, spread: 0.12 },
  // a wounded man's scream, played to its end; a wide pitch spread so no two men sound alike
  death: { start: 0.36, length: 1.6, gain: 0.7, voices: 3, spread: 0.14 },
  siren: { start: 1.6, length: 73, gain: 0.45, voices: 1 },
  bell: { start: 1.0, length: 3.6, gain: 0.55, voices: 1 },
  nuke: { start: 5.9, length: 14.5, gain: 2.2, voices: 1 },
} satisfies Record<string, Clip>;
type Name = keyof typeof CLIPS;

const MUTE_KEY = 'thrash.muted';
const VOLUME_KEY = 'thrash.volume';
/** Sound is off unless the player turned it on in Settings; the first click is the gesture that lets it play. */
let muted = ((): boolean => {
  try { return localStorage.getItem(MUTE_KEY) !== '0'; } catch { return true; }
})();
let volume = ((): number => {
  try { const v = Number(localStorage.getItem(VOLUME_KEY)); return Number.isFinite(v) && v > 0 && v <= 1 ? v : 0.8; } catch { return 0.8; }
})();

/** The volume, 0 to 1. */
export function getVolume(): number {
  return volume;
}

export function setVolume(v: number): void {
  volume = Math.max(0, Math.min(1, v));
  try { localStorage.setItem(VOLUME_KEY, String(volume)); } catch { /* storage unavailable: it lasts this visit */ }
  if (master && ctx) master.gain.setTargetAtTime(muted ? 0 : MASTER_GAIN * volume, ctx.currentTime, 0.02);
}

/** Creates the audio context inside a user gesture, so sound may play from then on. */
export function unlockAudio(): void {
  try { audio(); } catch { /* no audio here */ }
}

/** Whether all game sound is off. */
export function isMuted(): boolean {
  return muted;
}

/** Turns all game sound off or on, cutting off anything still sounding. */
export function setMuted(m: boolean): void {
  muted = m;
  try { localStorage.setItem(MUTE_KEY, m ? '1' : '0'); } catch { /* storage unavailable: it lasts this visit */ }
  if (master && ctx) master.gain.setTargetAtTime(m ? 0 : MASTER_GAIN * volume, ctx.currentTime, 0.02);
  if (m) try { speechSynthesis.cancel(); } catch { /* no speech here */ }
}

const MASTER_GAIN = 0.9;
let ctx: AudioContext | null = null;
let out: AudioNode | null = null;
let master: GainNode | null = null;
const buffers = new Map<Name, AudioBuffer>();
let loading = false;
/** The copies of each clip still sounding, and when each ends. */
const playing = new Map<Name, { src: AudioBufferSourceNode; end: number }[]>();

function audio(): AudioContext {
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new Ctor();
    // a gentle bus compressor: a nuke and a hundred rifles together still don't clip
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 12;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.25;
    master = ctx.createGain();
    master.gain.value = muted ? 0 : MASTER_GAIN * volume;
    comp.connect(master).connect(ctx.destination);
    out = comp;
  }
  if (ctx.state === 'suspended') void ctx.resume();
  if (!loading) {
    loading = true;
    for (const name of Object.keys(CLIPS) as Name[]) void load(name);
    // the C-RAM's stream is synthesized up front, so its first burst does not hitch
    cramStream = synthStream(ctx);
    cramEcho = synthEcho(ctx);
  }
  return ctx;
}

async function load(name: Name): Promise<void> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}sounds/${name}.mp3`);
    buffers.set(name, await audio().decodeAudioData(await res.arrayBuffer()));
  } catch {
    /* no such clip or no audio: that sound just stays silent */
  }
}

/**
 * Plays a clip now, or `delay` seconds from now. `length` overrides how much of it plays;
 * the end always fades rather than cuts. Does nothing if the clip hasn't loaded yet.
 */
function play(name: Name, opts: { gain?: number; rate?: number; length?: number; delay?: number; fadeIn?: number } = {}): void {
  if (muted) return;
  try {
    const c = audio();
    const buf = buffers.get(name);
    if (!buf || !out) return;
    const clip: Clip = CLIPS[name];
    const live = (playing.get(name) ?? []).filter(p => p.end > c.currentTime);
    // at the cap, the oldest copy makes way
    if (live.length >= clip.voices) {
      const old = live.shift()!;
      try { old.src.stop(); } catch { /* already stopped */ }
    }
    const spread = clip.spread ?? 0;
    const rate = (opts.rate ?? 1) * (1 + (Math.random() * 2 - 1) * spread);
    const length = Math.min(opts.length ?? clip.length, buf.duration - clip.start);
    const t0 = c.currentTime + (opts.delay ?? 0);
    const seconds = length / rate;
    const level = clip.gain * (opts.gain ?? 1) * (1 + (Math.random() * 2 - 1) * spread);
    const fade = Math.min(0.35, seconds * 0.3);
    const src = c.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const g = c.createGain();
    if (opts.fadeIn) {
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(level, t0 + opts.fadeIn);
    } else g.gain.setValueAtTime(level, t0);
    g.gain.setValueAtTime(level, t0 + seconds - fade);
    g.gain.linearRampToValueAtTime(0, t0 + seconds);
    src.connect(g).connect(out);
    src.start(t0, clip.start, length + 0.05);
    live.push({ src, end: t0 + seconds });
    playing.set(name, live);
  } catch {
    /* no audio here: the raid runs on in silence */
  }
}

const lastAt: Record<string, number> = {};
/** True when this kind of sound has not played in the last `ms` milliseconds. Keeps a big fight from turning into a wall of noise. */
function due(kind: string, ms: number): boolean {
  const now = performance.now();
  if (now - (lastAt[kind] ?? -Infinity) < ms) return false;
  lastAt[kind] = now;
  return true;
}

// ---- the C-RAM: synthesized, because what makes it a C-RAM is its rate ----------------
//
// An M61A1 gatling fires 75 rounds a second, too fast to hear as shots: it is one tearing
// "BRRRT", a buzz at 75 Hz made of overlapping cracks. The motor spins the barrels up over
// a fraction of a second, so each burst starts low and rises to pitch; the mount slews onto
// its target with an electric whine just before; and the burst rolls away as an echo off the
// land after it stops. One second of stream is synthesized once (exactly 75 rounds, so it
// loops without a seam), and each C-RAM firing gets its own voice that lasts while its
// shots keep coming.

/** Rounds a second, and how long the gun keeps sounding after its last shot before it is judged to have stopped. */
const CRAM_RPS = 75;
const CRAM_HOLD = 0.12;

let cramStream: AudioBuffer | null = null;
let cramEcho: AudioBuffer | null = null;
const cramVoices = new Map<number, { src: AudioBufferSourceNode; gain: GainNode; until: number }>();

/** A tiny seeded generator, so the synthesized stream is the same every time. */
function noiseSource(seed: number): () => number {
  let x = seed >>> 0 || 1;
  return () => { x ^= x << 13; x ^= x >>> 17; x ^= x << 5; return ((x >>> 0) / 4294967296) * 2 - 1; };
}

/** One second of the gun: 75 rounds, each a sharp crack, a burst of filtered noise and a low thump, slightly uneven. */
function synthStream(c: AudioContext): AudioBuffer {
  const sr = c.sampleRate;
  const per = Math.round(sr / CRAM_RPS);
  const len = per * CRAM_RPS;
  const buf = c.createBuffer(1, len, sr);
  const d = buf.getChannelData(0);
  const rnd = noiseSource(4500);
  const ring = Math.floor(per * 1.8);
  for (let r = 0; r < CRAM_RPS; r++) {
    const start = r * per + Math.round(rnd() * per * 0.05);
    const amp = 0.85 + 0.15 * rnd();
    let lp = 0;
    for (let k = 0; k < ring; k++) {
      const t = k / sr;
      lp += 0.32 * (rnd() - lp);
      const crack = t < 0.0007 ? rnd() * 1.4 : 0;
      const body = lp * Math.exp(-t / 0.005);
      const thump = Math.sin(2 * Math.PI * 92 * t) * Math.exp(-t / 0.011) * 0.7;
      const i = ((start + k) % len + len) % len;
      d[i] = d[i]! + amp * (crack + body + thump);
    }
  }
  let peak = 0;
  for (let i = 0; i < len; i++) peak = Math.max(peak, Math.abs(d[i]!));
  // driven a little into saturation: a gatling is all grit
  for (let i = 0; i < len; i++) d[i] = Math.tanh((d[i]! / peak) * 2.2) * 0.85;
  return buf;
}

/** The burst rolling away off the land: low rumble with two hard reflections, dying over a second and a half. */
function synthEcho(c: AudioContext): AudioBuffer {
  const sr = c.sampleRate;
  const len = Math.floor(sr * 1.6);
  const buf = c.createBuffer(1, len, sr);
  const d = buf.getChannelData(0);
  const rnd = noiseSource(1550);
  let lp = 0, lp2 = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    lp += 0.06 * (rnd() - lp);
    lp2 += 0.02 * (rnd() - lp2);
    const reflections = 0.9 * Math.exp(-Math.abs(t - 0.14) / 0.05) + 0.55 * Math.exp(-Math.abs(t - 0.38) / 0.08);
    d[i] = (lp * (0.5 * Math.exp(-t / 0.45) + reflections) + lp2 * 2.5 * Math.exp(-t / 0.7)) * Math.min(1, t / 0.01);
  }
  let peak = 0;
  for (let i = 0; i < len; i++) peak = Math.max(peak, Math.abs(d[i]!));
  for (let i = 0; i < len; i++) d[i] = (d[i]! / peak) * 0.8;
  return buf;
}

/** The mount slewing onto a target: an electric whine rising for a quarter of a second. */
function slewWhine(c: AudioContext, t0: number): void {
  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(420, t0);
  osc.frequency.exponentialRampToValueAtTime(1250, t0 + 0.22);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.05, t0 + 0.05);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
  osc.connect(g).connect(out!);
  osc.start(t0);
  osc.stop(t0 + 0.32);
}

/** Lets a voice go once its shots have stopped: a quick fade, then the echo. */
function cramRelease(id: number): void {
  const v = cramVoices.get(id);
  if (!v || !ctx) return;
  const now = ctx.currentTime;
  if (now < v.until) { setTimeout(() => cramRelease(id), Math.ceil((v.until - now) * 1000) + 10); return; }
  cramVoices.delete(id);
  v.gain.gain.cancelScheduledValues(now);
  v.gain.gain.setValueAtTime(v.gain.gain.value, now);
  v.gain.gain.linearRampToValueAtTime(0, now + 0.05);
  // the barrels run down: the last of the stream falls in pitch as it fades
  v.src.playbackRate.setValueAtTime(v.src.playbackRate.value, now);
  v.src.playbackRate.linearRampToValueAtTime(0.75, now + 0.05);
  v.src.stop(now + 0.06);
  if (cramEcho && out) {
    const src = ctx.createBufferSource();
    src.buffer = cramEcho;
    const g = ctx.createGain();
    g.gain.value = 0.5;
    src.connect(g).connect(out);
    src.start(now);
  }
}

/**
 * A C-RAM fired: start its stream if it was quiet, or keep it going. `id` tells the
 * guns apart, so two C-RAMs firing at once are two voices (three at most).
 */
export function cramFiring(id: number): void {
  if (muted) return;
  try {
    const c = audio();
    if (!out) return;
    cramStream ??= synthStream(c);
    cramEcho ??= synthEcho(c);
    const live = cramVoices.get(id);
    if (live) { live.until = c.currentTime + CRAM_HOLD; return; }
    if (cramVoices.size >= 3) return;
    const t0 = c.currentTime;
    slewWhine(c, t0);
    const src = c.createBufferSource();
    src.buffer = cramStream;
    src.loop = true;
    // the motor spins the barrels up: the stream starts slow and low and rises to its full 75 a second
    src.playbackRate.setValueAtTime(0.5, t0 + 0.06);
    src.playbackRate.exponentialRampToValueAtTime(1, t0 + 0.28);
    const gain = c.createGain();
    gain.gain.setValueAtTime(0.0001, t0 + 0.06);
    gain.gain.exponentialRampToValueAtTime(0.6, t0 + 0.12);
    src.connect(gain).connect(out);
    src.start(t0 + 0.06, Math.random());
    cramVoices.set(id, { src, gain, until: t0 + 0.06 + CRAM_HOLD });
    setTimeout(() => cramRelease(id), (0.06 + CRAM_HOLD) * 1000 + 10);
  } catch {
    /* no audio here */
  }
}

/** The base's alarm bell ringing as a raid begins. */
export function playAlarmBell(): void {
  play('bell');
}

/** A machine blowing up. Bursts closer than 120 ms are skipped. */
export function playExplosion(): void {
  if (!due('blast', 120)) return;
  play('explosion');
}

/** A bullet hitting someone. */
export function playGore(): void {
  if (!due('gore', 60)) return;
  play('gore');
}

/** A soldier going down screaming. Never two in quick succession, so a massacre is not one wall of noise. */
export function playDeathCry(): void {
  if (!due('cry', 220)) return;
  play('death', { gain: 0.85 + Math.random() * 0.3 });
}

// ---- weapons ----------------------------------------------------------------------------

/** Small arms: a rifle or a machine gun firing. */
export function playRifle(): void {
  if (!due('rifle', 55)) return;
  play('rifle', { length: 0.8 + Math.random() * 0.5 });
}

/** A sniper's rifle: a sharp crack rolling away over the field. */
export function playSniper(): void {
  if (!due('sniper', 150)) return;
  play('sniper');
}

/** A tank's or a ship's gun. */
export function playCannon(): void {
  if (!due('cannon', 140)) return;
  play('cannon');
}

/** A missile leaving its launcher: the roar of a rocket motor tearing away. */
export function playLaunch(): void {
  if (!due('launch', 160)) return;
  play('launch', { length: 2.6 + Math.random() * 1.2 });
}

/** A missile shot down in the air: a sharp, distant blast. */
export function playIntercept(): void {
  if (!due('intercept', 80)) return;
  play('intercept');
}

/** An EMP going off: a heavy electrical discharge. */
export function playEmp(): void {
  if (!due('emp', 300)) return;
  play('emp');
}

/**
 * A laser burning: no clip sounds like a directed-energy beam, so it is synthesized: a short
 * sizzling crack of noise through a rising bandpass (the beam forming), and a low electrical
 * thrum underneath it while the shot lasts.
 */
export function playLaser(): void {
  if (muted || !due('laser', 90)) return;
  try {
    const c = audio();
    if (!out) return;
    const t0 = c.currentTime;
    const len = Math.floor(c.sampleRate * 0.16);
    const buf = c.createBuffer(1, len, c.sampleRate);
    const d = buf.getChannelData(0);
    let band = 0;
    for (let i = 0; i < len; i++) {
      const t = i / c.sampleRate;
      // white noise, low-passed with a cut that rises through the shot: the crack brightens as the beam forms
      band += 0.35 * (Math.random() * 2 - 1 - band);
      d[i] = band * Math.min(1, t / 0.008) * Math.pow(1 - t / 0.16, 1.4);
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    const gain = c.createGain();
    gain.gain.setValueAtTime(0.55, t0);
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 1.2;
    bp.frequency.setValueAtTime(900, t0);
    bp.frequency.exponentialRampToValueAtTime(5600, t0 + 0.13);
    src.connect(bp).connect(gain).connect(out);
    src.start(t0);
    // the pumps: a low thrum under the crack, only while the beam is on
    const osc = c.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(58, t0);
    const og = c.createGain();
    og.gain.setValueAtTime(0.0001, t0);
    og.gain.exponentialRampToValueAtTime(0.045, t0 + 0.03);
    og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
    osc.connect(og).connect(out);
    osc.start(t0);
    osc.stop(t0 + 0.2);
  } catch {
    /* no audio here */
  }
}

/** An air-raid siren, wailing for the length of a nuke's countdown. */
export function playSiren(seconds: number): void {
  if (!due('siren', 1000)) return;
  play('siren', { length: Math.max(2, seconds), fadeIn: 0.6 });
}

/** A nuke going off: a huge blast, then the deep roll of its shockwave passing over. */
export function playNuke(): void {
  if (!due('nuke', 1500)) return;
  play('nuke');
  // the first, closest crack of it, and a deep rumble a moment later as the shockwave rolls over
  play('explosion', { gain: 1.3, rate: 0.75 });
  play('cannon', { gain: 1.1, rate: 0.5, delay: 0.25 });
  play('explosion', { gain: 0.9, rate: 0.55, delay: 0.9 });
}

/**
 * Calls a nuke in over the base's loudspeaker: "Warning. Nuclear launch detected." Spoken
 * by the browser's own voice, low and slow, under the siren.
 */
export function announceNuke(): void {
  if (muted || !due('announce', 4000)) return;
  try {
    const say = new SpeechSynthesisUtterance('Warning. Nuclear launch detected.');
    // a deep, measured voice if the machine has one
    const voices = speechSynthesis.getVoices();
    const pick = voices.find(v => /Daniel|Alex|Fred|Google UK English Male|Male/i.test(v.name) && v.lang.startsWith('en')) ?? voices.find(v => v.lang.startsWith('en'));
    if (pick) say.voice = pick;
    say.rate = 0.85;
    say.pitch = 0.6;
    speechSynthesis.speak(say);
  } catch {
    /* no speech here: the siren still sounds */
  }
}
